import {useCallback, useEffect, useState} from 'react';
import {useRouter} from 'next/router';

interface IRouterPushOptions {
    scroll?: boolean;
    shallow?: boolean;
}

let isNavigationLocked = false;
let isRequestLocked = false;
const lockListeners = new Set<(value: boolean) => void>();

const getIsFilterInteractionsLocked = () => {
    return isNavigationLocked || isRequestLocked;
};

const notifyLockListeners = () => {
    const lockValue = getIsFilterInteractionsLocked();
    lockListeners.forEach((listener) => listener(lockValue));
};

const setNavigationLocked = (value: boolean) => {
    if (isNavigationLocked === value) {
        return;
    }

    isNavigationLocked = value;
    notifyLockListeners();
};

export const setFilterRequestLocked = (value: boolean) => {
    if (isRequestLocked === value) {
        return;
    }

    isRequestLocked = value;
    notifyLockListeners();
};

export const useFilterInteractionsLock = () => {
    const [isLocked, setIsLocked] = useState(getIsFilterInteractionsLocked());

    useEffect(() => {
        lockListeners.add(setIsLocked);

        return () => {
            lockListeners.delete(setIsLocked);
        };
    }, []);

    return isLocked;
};

export const useFiltersRouterPush = () => {
    const router = useRouter();
    const pushRoute = useCallback(
        async (
            url: string | {pathname: string; query?: Record<string, any>},
            as?: string,
            options?: IRouterPushOptions,
        ) => {
            setNavigationLocked(true);

            try {
                const isNavigationSuccess = await router.push(url, as, options);

                if (!isNavigationSuccess) {
                    setNavigationLocked(false);
                }
            } catch (error) {
                setNavigationLocked(false);
                throw error;
            }

            setNavigationLocked(false);
        },
        [router],
    );

    return {
        pushRoute,
    };
};
